import { ASSETS } from '../Public/assets-data.js';

/**
 * ContentEntity — base class for every S3-backed content type
 * (events, services, blogs, categories, …).
 *
 * Each subclass declares its "macros" as statics:
 *   TYPE      short name (matches DataSourceConfig + seed-script keys)
 *   S3_PATH   dot-path in system_data_{lang}.json
 *   EMPTY     template for a brand-new entry (use `Type.empty()` to clone it)
 *   compare   default sort for lists
 *
 * Instances copy every raw field onto themselves (plus normalized/computed
 * fields set by the subclass constructor), so spreading (`{...entity}`),
 * JSON.stringify and Redux all behave exactly like the plain objects screens
 * consumed before. Methods are convenience on top — never store data only in
 * a getter.
 */
class ContentEntity {
    static TYPE = 'content';
    static S3_PATH = null;

    // Provenance hint — where an entry currently lives on its migration path.
    static SOURCES = { LOCAL: 'local', S3: 's3', ZOHO: 'zoho' };

    constructor(raw = {}) {
        Object.assign(this, raw);
    }

    // Image fields may hold an assets-data KEY or a full URL — keys resolve to URLs.
    static resolveImg(value) {
        return (typeof value === 'string' && ASSETS[value]) ? ASSETS[value] : value;
    }

    // Active = not explicitly disabled by any of the status flags in use.
    static isActiveRaw(raw) {
        return !!raw && raw.status !== false && raw.active !== false && raw.isActive !== false;
    }

    static from(raw) {
        return raw ? new this(raw) : null;
    }

    /**
     * Normalize a raw list: active-only, optional language filter (items
     * without a language field always pass), subclass sort order.
     */
    static list(rawList = [], { language = null } = {}) {
        return (rawList || [])
            .filter((raw) => this.isActiveRaw(raw))
            .filter((raw) => !language || !raw.language || raw.language === language)
            .map((raw) => this.from(raw))
            .sort((a, b) => this.compare(a, b));
    }

    // Default: preserve source order. Subclasses override (e.g. events by date).
    static compare() {
        return 0;
    }

    // Deep-cloned template for creating a new entry (admin editor / authoring).
    static empty() {
        return JSON.parse(JSON.stringify(this.EMPTY || {}));
    }

    isActive() {
        return ContentEntity.isActiveRaw(this);
    }

    toJSON() {
        return { ...this };
    }
}

export default ContentEntity;
